import { keepPreviousData, queryOptions, useQueries, useQuery } from '@tanstack/react-query';

import { LifecycleStatus } from '@ndvr/portfolio-api';
import {
    AggregatedTransactionReportView,
    BenchmarkComparisonSeries,
    IncomeReport,
    PerformanceData,
    PerformanceReport,
    PracticePulseView,
    StatisticsView,
} from '@ndvr/reporting-api';

import { ReportingApi } from '@/API';
import { formatDate, isDateStringWithinRange } from '@/utils';

import { useSelectedTenantGUIDs } from '../auth/tenant-selection';
import { AccountWithAumIncluded, useListAccountGroupProfilesWithAumIncluded } from './account-aum-queries';

export const useAggregatedInflows = (startDate?: Date, endDate?: Date) => {
    const tenantGUIDs = useSelectedTenantGUIDs();
    const formattedStartDate = formatDate(startDate, 'SERVER');
    const formattedEndDate = formatDate(endDate, 'SERVER');

    return useQuery({
        queryKey: ['tenants', tenantGUIDs, 'aggregatedInflows', formattedStartDate, formattedEndDate],
        queryFn: async () => {
            const [response] = await ReportingApi().getAggregatedInflowsForTenants(tenantGUIDs, {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
            });
            return response ?? null;
        },
        enabled: tenantGUIDs.length > 0,
    });
};

export const usePracticePulse = (aumStartDate?: Date, aumEndDate?: Date) => {
    const tenantGUIDs = useSelectedTenantGUIDs();
    const formattedStartDate = formatDate(aumStartDate, 'SERVER');
    const formattedEndDate = formatDate(aumEndDate, 'SERVER');

    return useQuery<PracticePulseView | null>({
        queryKey: ['tenants', tenantGUIDs, 'practicePulse', formattedStartDate, formattedEndDate],
        queryFn: async () => {
            const [response] = await ReportingApi().getPracticePulseForTenants(tenantGUIDs, {
                aumStartDate: formattedStartDate,
                aumEndDate: formattedEndDate,
            });
            return response ?? null;
        },
        enabled: tenantGUIDs.length > 0,
    });
};

// Household-scoped report params. groupByGuids takes ACCOUNT GUIDs (a "Portfolio" is an account
// in the reporting domain); empty or absent means the whole-household total.
type HouseholdReportParams = {
    householdGUID?: string;
    groupByGuids?: string[];
    // Widens the whole-household default (empty groupByGuids) to closed accounts as well.
    includeClosed?: boolean;
};

// Cache-key fragment for an account scoping: sorted, so the same set of accounts maps to one
// entry no matter which order they were picked in.
const accountScopeKey = (groupByGuids?: string[], includeClosed?: boolean) =>
    [...(groupByGuids ?? [])].sort().join(',') + (includeClosed ? '+closed' : '');

// Only sent when on: the server defaults to false, so the wire request for the live-only case stays
// exactly what it was before the flag existed.
const includeClosedParam = (includeClosed?: boolean) => (includeClosed ? true : undefined);

export const useAggregatedHoldingsReport = (
    { householdGUID, groupByGuids, includeClosed }: HouseholdReportParams,
    options?: { throwOnError?: boolean },
) => {
    return useQuery({
        ...options,
        queryKey: [
            'household',
            householdGUID,
            'aggregated-holdings-report',
            accountScopeKey(groupByGuids, includeClosed),
        ],
        queryFn: async () => {
            if (!householdGUID) return null;

            const [response] = await ReportingApi().getLatestHoldingsReportForHousehold(householdGUID, {
                groupByGuids: groupByGuids?.length ? groupByGuids : undefined,
                includeClosed: includeClosedParam(includeClosed),
            });
            return response ?? null;
        },
        enabled: !!householdGUID,
    });
};

// Income section of the performance report: income received over the previous 12 months and
// projected income by asset class and quarter, for the household or the selected accounts.
// Null when the household has no projection yet (the endpoint answers 204).
export const useHouseholdIncomeReport = ({ householdGUID, groupByGuids, includeClosed }: HouseholdReportParams) => {
    return useQuery<IncomeReport | null>({
        queryKey: ['household', householdGUID, 'income-report', accountScopeKey(groupByGuids, includeClosed)],
        queryFn: async () => {
            if (!householdGUID) return null;

            const [response] = await ReportingApi().getIncomeReportForHousehold(householdGUID, {
                groupByGuids: groupByGuids?.length ? groupByGuids : undefined,
                includeClosed: includeClosedParam(includeClosed),
            });
            return response ?? null;
        },
        enabled: !!householdGUID,
    });
};

type TransactionReportParams = HouseholdReportParams & {
    fromInclusive: Date;
    toInclusive: Date;
};

export const useAggregatedTransactionReport = ({
    householdGUID,
    groupByGuids,
    includeClosed,
    fromInclusive,
    toInclusive,
}: TransactionReportParams) => {
    return useQuery({
        queryKey: [
            'household',
            householdGUID,
            'aggregated-transaction-report',
            accountScopeKey(groupByGuids, includeClosed),
        ],
        queryFn: async (): Promise<AggregatedTransactionReportView[]> => {
            if (!householdGUID) return [];

            const [response] = await ReportingApi().getAggregatedTransactionReportsForHousehold(householdGUID, {
                groupByGuids: groupByGuids?.length ? groupByGuids : undefined,
                includeClosed: includeClosedParam(includeClosed),
            });
            return response ?? [];
        },
        select: (views) => views.filter((view) => isDateStringWithinRange(view.date, fromInclusive, toInclusive)),
        // Changing the account scope changes the queryKey; keep the previous ledger on screen
        // while the new scope loads so only the very first fetch shows the loading skeletons.
        placeholderData: keepPreviousData,
        enabled: !!householdGUID,
    });
};

type PerformanceReportParams = HouseholdReportParams & {
    fromInclusive?: Date;
    toInclusive?: Date;
};

// Shared builder for aggregated-performance-report queries. `enabled` is supplied by each
// caller (the household-total and per-portfolio scopings differ).
const aggregatedPerformanceReportQueryOptions = ({
    householdGUID,
    fromInclusive,
    toInclusive,
    groupByGuids,
    includeClosed,
    enabled,
}: PerformanceReportParams & { enabled: boolean }) =>
    queryOptions<PerformanceReport | null>({
        queryKey: [
            'household',
            householdGUID,
            'aggregated-performance-report',
            formatDate(fromInclusive, 'SERVER'),
            formatDate(toInclusive, 'SERVER'),
            accountScopeKey(groupByGuids, includeClosed),
        ],
        queryFn: async () => {
            if (!householdGUID) return null;

            const [response] = await ReportingApi().getAggregatedPerformanceReport(householdGUID, {
                fromInclusive: formatDate(fromInclusive, 'SERVER'),
                toInclusive: formatDate(toInclusive, 'SERVER'),
                groupByGuids: groupByGuids?.length ? groupByGuids : undefined,
                includeClosed: includeClosedParam(includeClosed),
            });
            return response ?? null;
        },
        enabled,
    });

export const useAggregatedPerformanceReport = (params: PerformanceReportParams, options?: { throwOnError?: boolean }) =>
    useQuery({
        ...aggregatedPerformanceReportQueryOptions({ ...params, enabled: !!params.householdGUID }),
        ...options,
    });

export const useAccountHistoricalPerformance = (
    { householdGUID, fromInclusive, toInclusive, groupByGuids, includeClosed }: PerformanceReportParams,
    options?: { throwOnError?: boolean },
) =>
    useQuery<PerformanceData[]>({
        ...options,
        queryKey: [
            'household',
            householdGUID,
            'account-historical-performance',
            formatDate(fromInclusive, 'SERVER'),
            formatDate(toInclusive, 'SERVER'),
            accountScopeKey(groupByGuids, includeClosed),
        ],
        queryFn: async () => {
            if (!householdGUID) return [];

            const [response] = await ReportingApi().getAccountHistoricalPerformance(householdGUID, {
                fromInclusive: formatDate(fromInclusive, 'SERVER'),
                toInclusive: formatDate(toInclusive, 'SERVER'),
                groupByGuids: groupByGuids?.length ? groupByGuids : undefined,
                includeClosed: includeClosedParam(includeClosed),
            });
            return response ?? [];
        },
        enabled: !!householdGUID,
    });

// Benchmark names available for comparison. Account-independent reference data; each
// name is both the identifier accepted by the comparison endpoint and the display label.
export const useAvailableBenchmarks = () =>
    useQuery<string[]>({
        queryKey: ['available-benchmarks'],
        queryFn: async () => {
            const [response] = await ReportingApi().getAvailableBenchmarks();
            return response ?? [];
        },
        staleTime: Infinity,
    });

type BenchmarkComparisonParams = PerformanceReportParams & {
    benchmarkNames: string[];
};

// Dollar-valued shadow-portfolio series (the account's cash flows invested in each
// benchmark), comparable with the historical-performance market values. Only the
// selected names are requested; benchmarks without data in the range are omitted
// by the server.
export const useBenchmarkComparison = ({
    householdGUID,
    fromInclusive,
    toInclusive,
    groupByGuids,
    includeClosed,
    benchmarkNames,
}: BenchmarkComparisonParams) =>
    useQuery<BenchmarkComparisonSeries[]>({
        queryKey: [
            'household',
            householdGUID,
            'benchmark-comparison',
            formatDate(fromInclusive, 'SERVER'),
            formatDate(toInclusive, 'SERVER'),
            accountScopeKey(groupByGuids, includeClosed),
            [...benchmarkNames].sort().join(','),
        ],
        queryFn: async () => {
            if (!householdGUID) return [];

            const [response] = await ReportingApi().getBenchmarkComparison(householdGUID, {
                benchmarkNames,
                fromInclusive: formatDate(fromInclusive, 'SERVER'),
                toInclusive: formatDate(toInclusive, 'SERVER'),
                groupByGuids: groupByGuids?.length ? groupByGuids : undefined,
                includeClosed: includeClosedParam(includeClosed),
            });
            return response ?? [];
        },
        // Adding/removing a benchmark changes the queryKey; keep the lines already on
        // screen while the widened selection loads instead of blanking them.
        placeholderData: keepPreviousData,
        enabled: !!householdGUID && benchmarkNames.length > 0,
    });

type StatisticsParams = HouseholdReportParams & {
    benchmarkName?: string;
};

// Risk measurements and relative statistics of the household against one benchmark over
// trailing 1Y/3Y/inception windows. Deliberately not scoped by the page date range: the
// windows are anchored server-side at the latest calendar month both series fully cover.
// Null when the account or benchmark has too little history for even one window.
const householdStatisticsQueryOptions = ({
    householdGUID,
    groupByGuids,
    includeClosed,
    benchmarkName,
}: StatisticsParams) =>
    queryOptions<StatisticsView | null>({
        queryKey: [
            'household',
            householdGUID,
            'statistics',
            accountScopeKey(groupByGuids, includeClosed),
            benchmarkName,
        ],
        queryFn: async () => {
            if (!householdGUID || !benchmarkName) return null;

            const [response] = await ReportingApi().getStatistics(householdGUID, {
                benchmarkName,
                groupByGuids: groupByGuids?.length ? groupByGuids : undefined,
                includeClosed: includeClosedParam(includeClosed),
            });
            return response ?? null;
        },
        // Switching benchmarks changes the queryKey; keep the tables on screen while the
        // new benchmark's statistics load instead of blanking them.
        placeholderData: keepPreviousData,
        enabled: !!householdGUID && !!benchmarkName,
    });

// The household's statistics against each benchmark, in order. The endpoint takes one benchmark,
// so this is one request per benchmark.
// ponytail: per-benchmark fan-out, bounded by the handful picked in the header; add a
// benchmarkNames list to getStatistics if that grows.
export const useHouseholdStatisticsForBenchmarks = ({
    benchmarkNames,
    ...params
}: Omit<StatisticsParams, 'benchmarkName'> & { benchmarkNames: string[] }) =>
    useQueries({
        queries: benchmarkNames.map((benchmarkName) => householdStatisticsQueryOptions({ ...params, benchmarkName })),
    });

type PortfolioPerformanceReportParams = {
    householdGUID: string;
    fromInclusive?: Date;
    toInclusive?: Date;
    accountGUIDs: string[];
};

// Performance report for a single portfolio (account group), aggregated from its
// member account GUIDs. In the reporting domain a "Portfolio" is an account, so the
// group's report is scoped by passing its account GUIDs as groupByGuids — passing the
// accountGroupGUID would return an all-zero report. An empty groupByGuids is treated as
// "all portfolios" (the household total), so the query is disabled when there are none.
//
// Plain factory (no `use` prefix) so it is safe to call inside useQueries().
export const portfolioPerformanceReportQueryOptions = ({
    householdGUID,
    fromInclusive,
    toInclusive,
    accountGUIDs,
}: PortfolioPerformanceReportParams) =>
    aggregatedPerformanceReportQueryOptions({
        householdGUID,
        fromInclusive,
        toInclusive,
        groupByGuids: accountGUIDs,
        enabled: accountGUIDs.length > 0,
    });

export interface HouseholdPortfolioPerformanceRow {
    portfolioGUID: string;
    name: string;
    accountCount: number;
    report: PerformanceReport | null;
}

type HouseholdPortfolioPerformancesParams = {
    householdGUID: string;
    fromInclusive?: Date;
    toInclusive?: Date;
    includeClosed?: boolean;
};

// The domain rule "reporting only covers REAL portfolios" — drafts and onboarding accounts have
// no report data — with closed accounts opted in by the caller. Decided per account rather than
// per group: a group's status is only the highest status among its accounts, so a live group can
// still hold drafts or closed accounts. Shared by the account selector, the scope note and the
// per-portfolio fan-out so all three agree on what the reports cover.
export const isReportableAccount = (account: AccountWithAumIncluded, includeClosed: boolean) =>
    account.lifecycleStatus === LifecycleStatus.REAL ||
    (includeClosed && account.lifecycleStatus === LifecycleStatus.CLOSED);

// The household's account groups narrowed to their reportable accounts, dropping groups left
// with none, plus the portfolio count.
const useReportableAccountGroups = (householdGUID: string, includeClosed: boolean) => {
    const { data: profiles, isPending } = useListAccountGroupProfilesWithAumIncluded(householdGUID);

    const accountGroups = (profiles ?? [])
        .map((profile) => ({
            ...profile,
            accounts: profile.accounts.filter((account) => isReportableAccount(account, includeClosed)),
        }))
        .filter((profile) => profile.accounts.length > 0);

    return {
        accountGroups,
        portfolioCount: accountGroups.length,
        isPending,
    };
};

// One performance report per reportable portfolio (account group) in the household, each
// scoped to that group's own reportable account GUIDs. Encapsulates the account filtering +
// per-group report fan-out so consumers only render.
export const useHouseholdPortfolioPerformances = ({
    householdGUID,
    fromInclusive,
    toInclusive,
    includeClosed = false,
}: HouseholdPortfolioPerformancesParams) => {
    const {
        accountGroups,
        portfolioCount,
        isPending: isProfilesLoading,
    } = useReportableAccountGroups(householdGUID, includeClosed);

    const reportQueries = useQueries({
        queries: accountGroups.map((profile) =>
            portfolioPerformanceReportQueryOptions({
                householdGUID,
                fromInclusive,
                toInclusive,
                accountGUIDs: profile.accounts.map((account) => account.accountGUID),
            }),
        ),
    });

    const rows: HouseholdPortfolioPerformanceRow[] = accountGroups.map((profile, index) => ({
        portfolioGUID: profile.accountGroup.accountGroupGUID,
        name: profile.accountGroup.accountGroupName.plainText,
        accountCount: profile.accounts.length,
        report: reportQueries[index]?.data ?? null,
    }));

    // A REAL group with no accounts yields a *disabled* query, which stays `isPending`
    // forever in react-query v5; key off `isLoading` (false when disabled) so one empty
    // portfolio doesn't pin the whole table in its loading state.
    return {
        rows,
        portfolioCount,
        isLoading: isProfilesLoading || reportQueries.some((query) => query.isLoading),
    };
};
